import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { Stack, useResponsiveValue } from '@superdispatch/ui';
import { Button, ButtonProps } from '@superdispatch/ui-lab';
import { ReactNode } from 'react';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionDTO } from 'shared/modules/permission/PermissionDTO';

interface ConfirmDialogContentProps<T extends keyof PermissionDTO> {
  title?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
  cancelButtonProps?: Partial<ButtonProps>;
  confirmButtonProps?: Partial<ButtonProps>;
  name?: T;
  permission?: keyof NonNullable<PermissionDTO[T]>;
}

export function ConfirmDialogContent<T extends keyof PermissionDTO>({
  name,
  title,
  onClose,
  children,
  permission,
  cancelButtonProps,
  confirmButtonProps,
}: ConfirmDialogContentProps<T>) {
  const screen = useResponsiveValue('mobile', 'tablet', 'desktop');
  const isMobile = screen === 'mobile' || screen === 'tablet';
  return (
    <>
      {title && (
        <DialogTitle disableTypography={true}>
          <Typography variant="h3">{title}</Typography>
        </DialogTitle>
      )}

      {children && <DialogContent>{children}</DialogContent>}

      <DialogActions>
        {isMobile ? (
          <Stack space="small">
            <Button
              fullWidth={true}
              variant="neutral"
              onClick={onClose}
              {...cancelButtonProps}
            >
              {cancelButtonProps?.children || 'Cancel'}
            </Button>
            {name && permission ? (
              <PermissionAction name={name} permission={permission}>
                <Button
                  fullWidth={true}
                  autoFocus={true}
                  {...confirmButtonProps}
                >
                  {confirmButtonProps?.children || 'Confirm'}
                </Button>
              </PermissionAction>
            ) : (
              <Button fullWidth={true} autoFocus={true} {...confirmButtonProps}>
                {confirmButtonProps?.children || 'Confirm'}
              </Button>
            )}
          </Stack>
        ) : (
          <>
            <Button variant="neutral" onClick={onClose} {...cancelButtonProps}>
              {cancelButtonProps?.children || 'Cancel'}
            </Button>
            {name && permission ? (
              <PermissionAction name={name} permission={permission}>
                <Button autoFocus={true} {...confirmButtonProps}>
                  {confirmButtonProps?.children || 'Confirm'}
                </Button>
              </PermissionAction>
            ) : (
              <Button autoFocus={true} {...confirmButtonProps}>
                {confirmButtonProps?.children || 'Confirm'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </>
  );
}

interface ConfirmDialogProps<T extends keyof PermissionDTO>
  extends
    ConfirmDialogContentProps<T>,
    Omit<DialogProps, 'title' | 'onClose'> {}

export function ConfirmDialog<T extends keyof PermissionDTO>({
  onClose,
  title,
  children,
  cancelButtonProps,
  confirmButtonProps,

  maxWidth = 'xs',
  fullWidth = true,

  ...props
}: ConfirmDialogProps<T>) {
  return (
    <Dialog
      {...props}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <ConfirmDialogContent
        title={title}
        onClose={onClose}
        cancelButtonProps={cancelButtonProps}
        confirmButtonProps={confirmButtonProps}
      >
        {children}
      </ConfirmDialogContent>
    </Dialog>
  );
}
